<xemex-page-header
  [title]="partnershipStore.detail()?.partnershipType ?? ('sidebarMenu.partnerships' | translate)"
  [subtitle]="
    partnershipStore.detail()
      ? partnershipStore.detail()!.ownerPartnerName +
        ' → ' +
        partnershipStore.detail()!.guestPartnerName
      : ''
  "
  [breadcrumbs]="breadcrumbs()"
  [loading]="partnershipStore.isLoadingDetail()"
  [withBackground]="true"
  [icon]="'handshake'"
>
  <xemex-text-button actions type="button" icon="description" (click)="onViewTerms()">
    {{ 'partnerships.detail.viewTerms' | translate }}
  </xemex-text-button>
  @if (canAccept()) {
    <xemex-filled-button
      actions
      type="button"
      icon="check"
      [loading]="partnershipStore.isProcessing()"
      (click)="onAccept()"
    >
      {{ 'partnerships.actions.accept.button' | translate }}
    </xemex-filled-button>
  }
  @if (canDecline()) {
    <xemex-outlined-button
      actions
      type="button"
      [disabled]="partnershipStore.isProcessing()"
      (click)="onDecline()"
    >
      {{ 'partnerships.actions.decline.button' | translate }}
    </xemex-outlined-button>
  }
  @if (canExtend()) {
    <xemex-outlined-button
      actions
      type="button"
      icon="event"
      [disabled]="partnershipStore.isProcessing()"
      (click)="onExtend()"
    >
      {{ 'partnerships.actions.extend.button' | translate }}
    </xemex-outlined-button>
  }
  @if (canRevoke()) {
    <xemex-filled-button
      actions
      type="button"
      intent="destructive"
      icon="block"
      [loading]="partnershipStore.isProcessing()"
      (click)="onRevoke()"
    >
      {{ 'partnerships.actions.revoke.button' | translate }}
    </xemex-filled-button>
  }
</xemex-page-header>

<xemex-page-body [scrollable]="true">
  <xemex-card-grid>
    <!-- General card -->
    <xemex-card
      [title]="'partnerships.detail.general' | translate"
      [loading]="partnershipStore.isLoadingDetail()"
    >
      <div class="grid grid-cols-2 gap-x-6 gap-y-4">
        <xemex-detail-field [label]="'partnerships.table.owner' | translate">
          <xemex-link-button (click)="onViewPartner(partnershipStore.detail()?.ownerPartnerOid)">
            {{ partnershipStore.detail()?.ownerPartnerName }}
          </xemex-link-button>
        </xemex-detail-field>
        <xemex-detail-field [label]="'partnerships.table.guest' | translate">
          <xemex-link-button (click)="onViewPartner(partnershipStore.detail()?.guestPartnerOid)">
            {{ partnershipStore.detail()?.guestPartnerName }}
          </xemex-link-button>
        </xemex-detail-field>
        <xemex-detail-field [label]="'partnerships.table.type' | translate">
          {{ partnershipStore.detail()?.partnershipType }}
        </xemex-detail-field>
        <xemex-detail-field [label]="'partnerships.table.status' | translate">
          {{ partnershipStore.detail()?.status }}
        </xemex-detail-field>
        <xemex-detail-field [label]="'partnerships.table.invitedAt' | translate">
          {{ partnershipStore.detail()?.invitedAt | date: 'dd-MM-yyyy' }}
        </xemex-detail-field>
        <xemex-detail-field [label]="'partnerships.detail.invitedBy' | translate">
          {{ partnershipStore.detail()?.invitedByUserName }}
        </xemex-detail-field>
        <xemex-detail-field [label]="'partnerships.table.endDate' | translate">
          {{ partnershipStore.detail()?.reviewDueDate | date: 'dd-MM-yyyy' }}
        </xemex-detail-field>
        @if (partnershipStore.detail()?.acceptedAt) {
          <xemex-detail-field [label]="'partnerships.detail.acceptedAt' | translate">
            {{ partnershipStore.detail()?.acceptedAt | date: 'dd-MM-yyyy' }}
          </xemex-detail-field>
        }
        @if (partnershipStore.detail()?.lastReviewedAt) {
          <xemex-detail-field [label]="'partnerships.detail.lastReviewedAt' | translate">
            {{ partnershipStore.detail()?.lastReviewedAt | date: 'dd-MM-yyyy' }}
          </xemex-detail-field>
        }
        @if (partnershipStore.detail()?.revokedAt) {
          <xemex-detail-field [label]="'partnerships.detail.revokedAt' | translate">
            {{ partnershipStore.detail()?.revokedAt | date: 'dd-MM-yyyy' }}
          </xemex-detail-field>
        }
      </div>
    </xemex-card>

    <!-- Installation groups card -->
    <xemex-card
      [title]="'partnerships.detail.installationGroups' | translate"
      [loading]="partnershipStore.isLoadingDetail()"
      [actionLabel]="
        canManageInstallationGroups()
          ? ('partnerships.addInstallationGroup.title' | translate)
          : undefined
      "
      actionIcon="add"
      (action)="onAddInstallationGroup()"
    >
      @if (!partnershipStore.detail()?.installationGroups?.length) {
        <div class="flex justify-center items-center py-6">
          <img src="assets/images/empty/document.svg" alt="" />
        </div>
      } @else {
        <div class="flex flex-col divide-y divide-gray-100">
          @for (
            group of partnershipStore.detail()?.installationGroups;
            track group.installationGroupOid
          ) {
            <div
              class="flex items-center justify-between py-2.5 cursor-pointer hover:bg-gray-50 -mx-1 px-1 rounded"
              (click)="onViewInstallationGroup(group.installationGroupOid)"
            >
              <div class="flex items-center gap-3">
                <div
                  class="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-none"
                >
                  <mat-icon class="text-gray-500 !text-sm !flex !items-center !justify-center">
                    workspaces
                  </mat-icon>
                </div>
                <div class="text-sm font-semibold">{{ group.name }}</div>
              </div>
              <div class="flex items-center gap-1">
                @if (canManageInstallationGroups()) {
                  <button
                    mat-icon-button
                    type="button"
                    [matTooltip]="'partnerships.removeInstallationGroup.tooltip' | translate"
                    (click)="onRemoveInstallationGroup(group, $event)"
                  >
                    <mat-icon class="text-gray-400 !text-lg">delete_outline</mat-icon>
                  </button>
                }
                <mat-icon class="text-gray-400 !text-lg">chevron_right</mat-icon>
              </div>
            </div>
          }
        </div>
      }
    </xemex-card>

    <!-- Audit log card -->
    @if (canViewAuditLog()) {
      <xemex-card
        [title]="'partnerships.detail.auditLog' | translate"
        [loading]="partnershipStore.isLoadingAuditLog()"
      >
        @if (!partnershipStore.auditLog().length) {
          <div class="flex justify-center items-center py-6">
            <img src="assets/images/empty/document.svg" alt="" />
          </div>
        } @else {
          <div class="flex flex-col divide-y divide-gray-100">
            @for (entry of partnershipStore.auditLog(); track entry.auditLogId) {
              <div class="flex items-start justify-between gap-3 py-2.5">
                <div class="flex items-start gap-3 min-w-0">
                  <div
                    class="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-none"
                  >
                    <mat-icon class="text-gray-500 !text-sm !flex !items-center !justify-center">
                      history
                    </mat-icon>
                  </div>
                  <div class="min-w-0">
                    <div class="text-sm font-semibold">{{ eventLabel(entry.eventType) }}</div>
                    @if (entry.actorUserName || entry.actorPartnerName) {
                      <div class="text-xs text-gray-500 truncate">
                        {{ entry.actorUserName ?? entry.actorPartnerName }}
                        @if (entry.actorUserName && entry.actorPartnerName) {
                          · {{ entry.actorPartnerName }}
                        }
                      </div>
                    }
                    @if (entry.subjectPartnerOid || entry.installationGroupOid) {
                      <div class="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs">
                        @if (entry.subjectPartnerOid) {
                          <xemex-link-button
                            icon="business"
                            (click)="onViewPartner(entry.subjectPartnerOid)"
                          >
                            {{
                              entry.subjectPartnerName ?? ('partnerships.table.guest' | translate)
                            }}
                          </xemex-link-button>
                        }
                        @if (entry.installationGroupOid) {
                          <xemex-link-button
                            icon="workspaces"
                            (click)="onViewInstallationGroup(entry.installationGroupOid)"
                          >
                            {{ installationGroupName(entry.installationGroupOid) }}
                          </xemex-link-button>
                        }
                      </div>
                    }
                  </div>
                </div>
                <div class="text-xs text-gray-400 flex-none whitespace-nowrap">
                  {{ entry.createdAt | date: 'dd-MM-yyyy HH:mm' }}
                </div>
              </div>
            }
          </div>
          @if (auditLogCount() > pageSize) {
            <xemex-table-pagination
              [page]="auditLogPage()"
              [totalPages]="auditLogTotalPages()"
              [total]="auditLogCount()"
              [pageSize]="pageSize"
              [compact]="true"
              (pageChange)="onAuditLogPageChange($event)"
            ></xemex-table-pagination>
          }
        }
      </xemex-card>
    }
  </xemex-card-grid>
</xemex-page-body>
